import { InternalAxiosRequestConfig } from "axios"

import { HydraError } from "@modules/frontend-definitions/src"

import { touchBypassKey } from "./storage"
import { getMaintenanceModeConfig } from "../config"

/**
 * Configures the given axios request config according to the current
 * state of the "bypass mode".
 *
 * Appends a "bypass maintenance mode" http header with key, if present in local browser store.
 * Circumvents a possible "maintenance, returning 503" mode in the backend/API.
 * Storage item is currently set via a "secret" parameter,
 * see usage of `MaintenanceBypass.useBypassKey()` in /pages/login.tsx.
 * Should allow a platform manager / devop / coder / tester to bypass the maintenance mode to test maintenance
 * progress by using the client, while the client is blocked for all other users.
 *
 * @param config the axios config to be modified
 */
export const configureAxiosRequest = (config: InternalAxiosRequestConfig): void => {

  const bypassMaintenanceKey = touchBypassKey()
  if (bypassMaintenanceKey) {
    config.headers.set(getMaintenanceModeConfig().bypassHttpHeaderName, bypassMaintenanceKey)
  }
}

/**
 * Checks if an `AxiosError` indicates that the backend is in maintenance mode.
 *
 * @param err the AxiosError to be checked
 * @returns true, if `err` indicates that the backend is in maintenance mode
 */
export const backendIsInMaintenance = (err: HydraError): boolean => {
  // early return on non 503 error
  if (err.response.status !== 503) {
    return false
  }
  // early return on non "maintenance" error
  if (err.response.data.title !== getMaintenanceModeConfig().maintenanceModeErrorTitle) {
    return false
  }

  // Debug check for correct mode of work:
  if (
    // if bypass key is set, backend should NOT respond with 503
    touchBypassKey()
  ) {
    getMaintenanceModeConfig().logUnhandledUnexpectedError(
      "Backend response is 503, although client is in 'bypass mode': " + err.message,
      "maintenance-mode"
    )
  }
  return true
}
